import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiEdit2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import { formatMoney, formatDate } from '../../utils/format';

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.employees.get(id).then(({ data }) => {
      setEmp(data.employee || data);
    }).catch((err) => showError('Failed to load employee', extractError(err))).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loader />;
  if (!emp) return null;

  const shift = emp.shift || null;
  const assignments = emp.assignments || [];
  const stitches = Array.isArray(emp.stitches) ? emp.stitches : (emp.stitches && emp.stitches.data) || [];

  return (
    <div className="page">
      <PageHeader
        title={emp.name}
        subtitle={`Machine ID: ${emp.machine_id || '-'} | ${emp.employee_type || ''}`}
        crumbs={[{ label: 'HR', to: '/employees' }, { label: 'Employees', to: '/employees' }, { label: emp.name }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/employees')}><FiArrowLeft /> Back</button>
            <button className="btn btn-primary" onClick={() => navigate(`/employees/${emp.id}/edit`)}><FiEdit2 /> Edit</button>
          </>
        }
      />

      <div className="grid-2" style={{ alignItems: 'start' }}>
        {/* Basic Info */}
        <div className="card">
          <div className="card-header"><div className="card-title">Basic Information</div></div>
          <div className="card-body">
            <div className="detail-grid">
              <div className="detail-item"><div className="d-label">Machine ID</div><div className="d-value">{emp.machine_id || '-'}</div></div>
              <div className="detail-item"><div className="d-label">Name</div><div className="d-value">{emp.name || '-'}</div></div>
              <div className="detail-item"><div className="d-label">Type</div><div className="d-value"><StatusBadge status={emp.employee_type} /></div></div>
              <div className="detail-item"><div className="d-label">Designation</div><div className="d-value">{emp.designation || '-'}</div></div>
              <div className="detail-item"><div className="d-label">CNIC</div><div className="d-value">{emp.cnic || '-'}</div></div>
              <div className="detail-item"><div className="d-label">Phone</div><div className="d-value">{emp.phone || '-'}</div></div>
              <div className="detail-item"><div className="d-label">Basic Salary</div><div className="d-value">{formatMoney(emp.basic_salary)}</div></div>
              <div className="detail-item"><div className="d-label">Joining Date</div><div className="d-value">{formatDate(emp.joining_date)}</div></div>
              <div className="detail-item"><div className="d-label">Status</div><div className="d-value"><StatusBadge status={emp.is_active ? 'active' : 'inactive'} /></div></div>
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}><div className="d-label">Address</div><div className="d-value">{emp.address || '-'}</div></div>
            </div>
          </div>
        </div>

        {/* Shift Info */}
        <div className="card">
          <div className="card-header"><div className="card-title">Shift Information</div></div>
          <div className="card-body">
            {shift ? (
              <div className="detail-grid">
                <div className="detail-item"><div className="d-label">Shift Name</div><div className="d-value">{shift.name || '-'}</div></div>
                <div className="detail-item"><div className="d-label">Short Name</div><div className="d-value">{shift.short_name || '-'}</div></div>
                <div className="detail-item"><div className="d-label">Start Time</div><div className="d-value">{shift.start_time || '-'}</div></div>
                <div className="detail-item"><div className="d-label">End Time</div><div className="d-value">{shift.end_time || '-'}</div></div>
                <div className="detail-item"><div className="d-label">Status</div><div className="d-value"><StatusBadge status={shift.is_active ? 'active' : 'inactive'} /></div></div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No shift assigned</div>
            )}
          </div>
        </div>
      </div>

      {/* CNIC Images */}
      {(emp.cnic_front || emp.cnic_back) && (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-header"><div className="card-title">CNIC Images</div></div>
          <div className="card-body">
            <div className="grid-2">
              {emp.cnic_front && (
                <div>
                  <div className="d-label" style={{ marginBottom: 8 }}>CNIC Front</div>
                  <img src={emp.cnic_front} alt="CNIC Front" style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 'var(--radius)', border: '1px solid var(--border)' }} />
                </div>
              )}
              {emp.cnic_back && (
                <div>
                  <div className="d-label" style={{ marginBottom: 8 }}>CNIC Back</div>
                  <img src={emp.cnic_back} alt="CNIC Back" style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 'var(--radius)', border: '1px solid var(--border)' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Machine Assignments */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Machine Assignments</div></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Machine</th><th>Heads</th><th>Assigned Date</th><th>Released Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td>{a.machine ? a.machine.name : '-'}</td>
                  <td>{a.machine ? a.machine.head_type : '-'}</td>
                  <td>{formatDate(a.assigned_date)}</td>
                  <td>{a.released_date ? formatDate(a.released_date) : '-'}</td>
                  <td><StatusBadge status={a.released_date ? 'inactive' : 'active'} label={a.released_date ? 'Released' : 'Assigned'} /></td>
                </tr>
              ))}
              {assignments.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No assignments</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Stitches */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Recent Stitches</div></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Date</th><th>Machine</th><th className="table-num">Stitches</th><th>Special</th><th>Remarks</th></tr>
            </thead>
            <tbody>
              {stitches.slice(0, 10).map((s) => (
                <tr key={s.id}>
                  <td>{formatDate(s.date)}</td>
                  <td>{s.machine ? s.machine.name : '-'}</td>
                  <td className="table-num">{s.stitch_count}</td>
                  <td>{s.special_thread ? <StatusBadge status="posted" label="Yes" /> : 'No'}</td>
                  <td>{s.remarks || '-'}</td>
                </tr>
              ))}
              {stitches.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No stitches recorded</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
